import { policyType } from '@ourbranch/lookups';

import { currencyFormatter } from 'core/helpers/formatters';

// Agency reps are stored as `<affinity>_<agent code>_<username>` (e.g. MATIC1_293_lcunningham).
// Only the username distinguishes reps within an agency, and the full value pushed the search
// results table past the viewport once a Price column was added (#35673). The caller shows the
// full rep as a tooltip.
export const abbreviateOfferRep = (rep) => {
  if (!rep) {
    return '';
  }
  const username = rep.slice(rep.lastIndexOf('_') + 1);
  if (username === rep || username === '') {
    return rep;
  }
  return `…${username}`;
};

// The products each option type covers, in the order the row shows them, with the RateOption
// field holding that product's term total and the length of that term. Auto is a 6-month term and
// everything else 12, as the offer sidebar's price detail labels them. Note AR reads renters first
// here even though the domain `policyTypeLines.AR` is ['A', 'R'] — this is display order only.
const home = { name: 'Home', total: 'homeTotal', termMonths: 12 };
const auto = { name: 'Auto', total: 'autoTotal', termMonths: 6 };
const renters = { name: 'Renters', total: 'rentersTotal', termMonths: 12 };
const condo = { name: 'Condo', total: 'condoTotal', termMonths: 12 };
const farm = { name: 'Farm', total: 'farmTotal', termMonths: 12 };
const OPTION_PRODUCTS = {
  [policyType.Home]: [home],
  [policyType.HomeBundle]: [home],
  [policyType.Auto]: [auto],
  [policyType.AutoBundle]: [auto],
  [policyType.Renters]: [renters],
  [policyType.Condo]: [condo],
  [policyType.Farm]: [farm],
  // FarmHome / FarmRental are variants of the one farm offer: named differently, priced as farm.
  // `name` groups products into bundles (FarmHome + Auto is Home + Auto) and is what the legacy
  // Eligible Offers column prints; `label` is what an agent sees on the icon, so a farm offer
  // doesn't read as a Branch homeowners or renters one.
  [policyType.FarmHome]: [{ ...farm, name: 'Home', label: 'Farm Home' }],
  [policyType.FarmRental]: [{ ...farm, name: 'Rental', label: 'Farm Rental' }],
  [policyType.HABundle]: [home, auto],
  [policyType.CABundle]: [condo, auto],
  [policyType.ARBundle]: [renters, auto],
  [policyType.FABundle]: [farm, auto]
};
const productsOf = (option) => OPTION_PRODUCTS[option?.type] ?? [];

// Which bundle the row is labelled as when an offer's options cover more than one, and which
// monoline when they cover no bundle at all.
const BUNDLE_PRECEDENCE = [policyType.HABundle, policyType.CABundle, policyType.ARBundle, policyType.FABundle];
const MONOLINE_PRECEDENCE = ['Home', 'Auto', 'Renters', 'Rental', 'Condo', 'Farm'];

// The products the row shows, in display order: the union of products across every option, so
// Home and Auto monolines alone still show as Home + Auto. `bundleType` is set when the row
// shows a bundle, whether or not an option of that type actually exists.
const resolveLabelledProducts = (options) => {
  const named = new Map(options.flatMap(productsOf).map((product) => [product.name, product]));
  const bundleType = BUNDLE_PRECEDENCE.find((type) =>
    OPTION_PRODUCTS[type].every((product) => named.has(product.name))
  );
  if (bundleType) {
    return { bundleType, products: OPTION_PRODUCTS[bundleType] };
  }
  const name = MONOLINE_PRECEDENCE.find((candidate) => named.has(candidate));
  return { bundleType: undefined, products: name ? [named.get(name)] : [] };
};

// The first single-product option named `name`. Its own product is what the row shows, so a
// FarmHome ("Home") reads farmTotal, not homeTotal, and is labelled Farm Home.
const findMonolineOption = (options, name) =>
  options.find((candidate) => {
    const products = productsOf(candidate);
    return products.length === 1 && products[0].name === name;
  });

// The products the Offer column shows, in display order (renters & auto → [renters, auto]), each
// with its term length and formatted term price. A bundle option prices every product from its
// own (discounted) totals; when the row is the union of separate monolines, each product is
// priced from its monoline, so a Home + Auto row never shows just one of them. Prices come from
// the term totals, not `monthly`: a bundle's `monthly` is both policies' monthly prices added
// together (homeTotal / 12 + autoTotal / 6), so it can't be split back into each product's price.
// `price` is undefined when the option carries no total — documents indexed before #35673 (and
// anything older than its backfill window), or a bundle whose auto line was dropped — so the
// product still shows, with no price rather than $0. `formatOptions` go to currencyFormatter.
export const deriveOfferProducts = (options, formatOptions) => {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }
  const { bundleType, products } = resolveLabelledProducts(options);
  const bundle = bundleType ? options.find((option) => option?.type === bundleType) : undefined;
  return products.map((labelledProduct) => {
    const monolineOption = bundle ? undefined : findMonolineOption(options, labelledProduct.name);
    const product = monolineOption ? productsOf(monolineOption)[0] : labelledProduct;
    const total = (bundle ?? monolineOption)?.[product.total];
    return {
      name: product.name,
      label: product.label ?? product.name,
      termMonths: product.termMonths,
      price: typeof total === 'number' && total > 0 ? currencyFormatter(total, formatOptions) : undefined
    };
  });
};

// The pre-#36395 Eligible Offers and Price columns, still shown on brands without the
// offerSearchRedesign flag (legacy-offer-item.jsx). Delete with that file.
export const deriveEligibleOffers = (options) =>
  deriveOfferProducts(options)
    .map((product) => product.name)
    .join(' & ') || 'N/A';

// Whole dollars, and only the products that have a price, one line each in Eligible Offers order.
export const deriveOfferTermPrices = (options) =>
  deriveOfferProducts(options, { removeDecimal: true })
    .map((product) => product.price)
    .filter(Boolean);
